import React, { useState, useCallback } from 'react';
import { getCanonicalContext, FieldContextOverrides } from '../../utils/faqApi';
import FieldEditor, { FullFieldContext } from './field-context-tuning/FieldEditor';
import LiveTestPanel from './field-context-tuning/LiveTestPanel';
import ValidationPanel from './field-context-tuning/ValidationPanel';
import DiffExportPanel from './field-context-tuning/DiffExportPanel';
import ReactSnippetPanel from './field-context-tuning/ReactSnippetPanel';

// ---------------------------------------------------------------------------
// Topic list — mirrors TOPIC_FIELD_CONTEXTS keys in lambda_function_dual.py
// ---------------------------------------------------------------------------
const TOPICS: Array<{ value: string; label: string }> = [
  { value: 'foreign-contact-reporting', label: 'Foreign Contact Reporting' },
  { value: 'foreign-contact-reporting-sead3', label: 'Foreign Contact (SEAD-3)' },
  { value: 'foreign-travel-reporting', label: 'Foreign Travel Reporting' },
  { value: 'foreign-activities-reporting', label: 'Foreign Activities Reporting' },
  { value: 'financial-matters-reporting', label: 'Financial Matters Reporting' },
  { value: 'legal-matters-reporting', label: 'Legal Matters Reporting' },
  { value: 'personal-relationships-reporting', label: 'Personal Relationships Reporting' },
  { value: 'media-contact-reporting', label: 'Media Contact Reporting' },
  { value: 'substance-treatment-reporting', label: 'Substance Treatment Reporting' },
  { value: 'reportable-actions-by-others', label: 'Reportable Actions by Others' },
];

const EMPTY_CONFIG: FullFieldContext = {
  fieldType: '',
  fieldDescription: '',
  significance: '',
  commonRejectionReasons: [],
  interviewQuestions: [],
  finalPrompt: '',
  helpText: '',
  bestPractices: [],
  warningFlags: [],
  expectedLength: 'moderate',
  requiresSpecificity: false,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const FieldContextTuning: React.FC = () => {
  const [selectedTopic, setSelectedTopic] = useState('');
  const [canonicalConfig, setCanonicalConfig] = useState<FullFieldContext | null>(null);
  const [editedConfig, setEditedConfig] = useState<FullFieldContext | null>(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [debugConfig, setDebugConfig] = useState(false);

  const handleTopicChange = useCallback(async (topic: string) => {
    setSelectedTopic(topic);
    setCanonicalConfig(null);
    setEditedConfig(null);
    setFetchError(null);

    if (!topic) return;

    setFetchLoading(true);
    try {
      // Fetch canonical config from the dev Lambda — keeps prompt directives
      // off the prod response path. Requires faq-lambda-dev to be deployed.
      const response = await getCanonicalContext(topic);
      if (response.success && response.canonical_context) {
        const full: FullFieldContext = { ...EMPTY_CONFIG, ...response.canonical_context } as FullFieldContext;
        setCanonicalConfig(full);
        setEditedConfig(JSON.parse(JSON.stringify(full))); // deep copy
      } else {
        setFetchError(response.error || 'Dev Lambda did not return canonical_context. Ensure faq-lambda-dev is deployed (deploy_dual.sh --include-dev).');
      }
    } catch (e) {
      setFetchError(e instanceof Error ? e.message : 'Failed to fetch canonical config');
    } finally {
      setFetchLoading(false);
    }
  }, []);

  const handleReset = () => {
    if (canonicalConfig) {
      setEditedConfig(JSON.parse(JSON.stringify(canonicalConfig)));
    }
  };

  const hasChanges =
    canonicalConfig &&
    editedConfig &&
    JSON.stringify(canonicalConfig) !== JSON.stringify(editedConfig);

  return (
    <div className="flex-1 p-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Dev tool banner */}
        <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-3">
          <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-amber-900">Developer Tool</p>
            <p className="text-sm text-amber-800 mt-0.5">
              Changes made here are not persisted. Use the Python diff export to share updates with the
              deployment team for promotion to <code className="bg-amber-100 px-1 rounded">TOPIC_FIELD_CONTEXTS</code> in{' '}
              <code className="bg-amber-100 px-1 rounded">lambda_function_dual.py</code>.
              The Validate Config feature requires <code className="bg-amber-100 px-1 rounded">faq-lambda-dev</code> to be deployed.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-gray-900">Field Context Tuning</h1>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <span className="text-sm text-gray-600">Debug console logging</span>
            <button
              role="switch"
              aria-checked={debugConfig}
              onClick={() => setDebugConfig((v) => !v)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-1 ${
                debugConfig ? 'bg-amber-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                  debugConfig ? 'translate-x-4' : 'translate-x-1'
                }`}
              />
            </button>
          </label>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          Edit, live-test, and validate field context configurations before promoting them to the Lambda.
        </p>

        {/* Topic selector */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 flex-shrink-0">Topic</label>
            <select
              value={selectedTopic}
              onChange={(e) => handleTopicChange(e.target.value)}
              className="flex-1 text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 max-w-md"
            >
              <option value="">— Select a topic —</option>
              {TOPICS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            {fetchLoading && (
              <span className="text-sm text-gray-400 italic">Fetching canonical config…</span>
            )}
            {fetchError && (
              <span className="text-sm text-red-600">{fetchError}</span>
            )}
          </div>
        </div>

        {/* Main 2-column layout */}
        {selectedTopic && canonicalConfig && editedConfig && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT — Config editor */}
            <div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-semibold text-gray-900">Config Editor</h2>
                  <button
                    onClick={handleReset}
                    disabled={!hasChanges}
                    className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-40 disabled:cursor-not-allowed border border-gray-300 px-2 py-1 rounded hover:bg-gray-50"
                  >
                    Reset to canonical
                  </button>
                </div>
                <FieldEditor
                  config={editedConfig}
                  canonical={canonicalConfig}
                  onChange={setEditedConfig}
                />
              </div>
            </div>

            {/* RIGHT — Live test + Validate & Export */}
            <div className="space-y-6">
              {/* Live test */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-4">Live Test</h2>
                <LiveTestPanel
                  topic={selectedTopic}
                  canonical={canonicalConfig}
                  edited={editedConfig}
                  debugConfig={debugConfig}
                />
              </div>

              {/* Validate & Export */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-4">Validate Config</h2>
                <ValidationPanel topic={selectedTopic} editedConfig={editedConfig} />

                <div className="mt-6 pt-5 border-t border-gray-200">
                  <h2 className="text-base font-semibold text-gray-900 mb-4">Export Diff</h2>
                  <DiffExportPanel
                    topic={selectedTopic}
                    canonical={canonicalConfig}
                    edited={editedConfig}
                  />
                </div>
              </div>

              {/* React Component Snippet */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-base font-semibold text-gray-900 mb-4">React Component Snippet</h2>
                <ReactSnippetPanel
                  topic={selectedTopic}
                  canonical={canonicalConfig}
                  edited={editedConfig}
                />
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!selectedTopic && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-16 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-gray-500 text-sm">Select a topic above to load its canonical config and start tuning.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldContextTuning;
